import { useEffect, useState } from 'react';
import { Check, Copy, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useGeneratePreviewToken } from '@/lib/posts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  postId: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Public site that renders the preview page (news1live.com/preview/<token>).
 *  Overridable via VITE_PUBLIC_SITE_URL for staging; trailing slash trimmed. */
const PUBLIC_SITE_URL = (import.meta.env.VITE_PUBLIC_SITE_URL ?? 'https://news1live.com').replace(
  /\/$/,
  '',
);

/**
 * Generates + displays a shareable preview URL for the post. The URL points at
 * the PUBLIC site's preview route (news1live.com/preview/<token>) — the public
 * site fetches the draft from our API via the token, so the recipient sees the
 * real, styled article (not the admin SPA / raw JSON).
 *
 * Token TTL is 7 days (set server-side). The dialog re-mints on open
 * so reopening always gets a fresh expiry — the previous token is NOT
 * revoked (revocation requires rotating COOKIE_SECRET) but the recipient
 * always gets the longest possible window from the most recent share.
 */
export function PreviewShareDialog({ open, onOpenChange, postId }: Props) {
  const generate = useGeneratePreviewToken();
  const [copied, setCopied] = useState(false);

  // Re-mint every open. Mutation state is the source of truth — no
  // local copy of the token so closing+reopening yields a fresh URL
  // with maximum remaining TTL. Intentionally excluding `generate`
  // from deps — adding it would re-fire the mutation on every render
  // since the hook returns a new object each time.
  const { mutate } = generate;
  useEffect(() => {
    if (open) {
      setCopied(false);
      mutate(postId);
    }
  }, [open, postId, mutate]);

  const token = generate.data?.token;
  const expiresAt = generate.data?.expiresAt;
  const url = token ? `${PUBLIC_SITE_URL}/preview/${token}` : '';

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail in non-secure contexts or denied perms —
      // fall through; user can still select + Ctrl+C from the input.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5 text-slate-400" />
            แชร์ลิงก์พรีวิว
          </DialogTitle>
          <DialogDescription>
            ลิงก์ใช้ได้กับใครก็ตามที่ได้รับ — ไม่ต้องเข้าสู่ระบบ.
            เหมาะสำหรับส่งให้บอสหรือทีมรีวิวก่อนเผยแพร่
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {generate.isPending && <p className="text-sm text-slate-500">กำลังสร้างลิงก์...</p>}
          {generate.isError && (
            <p className="text-destructive text-sm">สร้างลิงก์ไม่สำเร็จ — ลองอีกครั้ง</p>
          )}
          {token && url && (
            <>
              <div className="flex gap-2">
                <Input value={url} readOnly className="font-mono text-xs" />
                <Button type="button" variant="outline" size="sm" onClick={() => void copy()}>
                  {copied ? (
                    <>
                      <Check className="mr-1.5 h-3.5 w-3.5" />
                      คัดลอกแล้ว
                    </>
                  ) : (
                    <>
                      <Copy className="mr-1.5 h-3.5 w-3.5" />
                      คัดลอก
                    </>
                  )}
                </Button>
              </div>
              {expiresAt && (
                <p className="text-[11px] text-slate-500">
                  ลิงก์หมดอายุ {formatDate(expiresAt)} (7 วัน)
                </p>
              )}
              <p className="text-[11px] text-slate-400">
                หมายเหตุ: ผู้รับเห็นเนื้อหา draft ปัจจุบัน — แก้+save แล้ว reload ลิงก์จะอัปเดต
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
